import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  FileImage,
  PackagePlus,
  Save,
  Sparkles,
} from "lucide-react";
import api from "../api/api";
import { useDialog } from "../components/DialogProvider";
import {
  getJenisFromKode,
  getKategoriFromKode,
  getSeriFromKode,
  getSizeFromKode,
} from "../utils/productCode";

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-zinc-500">{label}</label>
      {children}
      {hint ? <div className="mt-1.5 text-[11px] font-medium text-zinc-400">{hint}</div> : null}
    </div>
  );
}

function DerivedTile({ label, value }) {
  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 shadow-sm">
      <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{label}</div>
      <div className="mt-1 min-h-[20px] truncate text-[13px] font-bold text-zinc-900">{value || "-"}</div>
    </div>
  );
}

export default function AddProduct() {
  const navigate = useNavigate();
  const dialog = useDialog();
  const [kodeBarang, setKodeBarang] = useState("");
  const [namaBarang, setNamaBarang] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [kodeWarning, setKodeWarning] = useState("");
  const [checkingKode, setCheckingKode] = useState(false);

  const derived = useMemo(() => {
    return {
      kategori: getKategoriFromKode(kodeBarang),
      jenis: getJenisFromKode(kodeBarang),
      seri: getSeriFromKode(kodeBarang),
      size: getSizeFromKode(kodeBarang),
    };
  }, [kodeBarang]);

  const canSubmit = kodeBarang.trim() && namaBarang.trim() && !kodeWarning && !checkingKode;

  useEffect(() => {
    const kode = kodeBarang.trim().toUpperCase();
    setKodeWarning("");
    setError("");
    setSuccess("");

    if (!kode) {
      setCheckingKode(false);
      return;
    }

    const handle = setTimeout(async () => {
      try {
        setCheckingKode(true);
        const res = await api.get(`/products/${encodeURIComponent(kode)}`);
        const found = res?.data && Object.keys(res.data).length > 0;
        if (found) {
          setKodeWarning("Kode barang tersebut sudah ada");
        } else {
          setKodeWarning("");
        }
      } catch {
        setKodeWarning("");
      } finally {
        setCheckingKode(false);
      }
    }, 450);

    return () => clearTimeout(handle);
  }, [kodeBarang]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const kode = kodeBarang.trim().toUpperCase();
    const nama = namaBarang.trim();
    const ok = await dialog.confirm(
      `Yakin mau tambah produk baru?\n\nKode: ${kode}\nNama: ${nama}`,
      { title: "Tambah Produk", confirmText: "Simpan", cancelText: "Batal" }
    );
    if (!ok) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const form = new FormData();
      form.append("kode_barang", kode);
      form.append("nama_barang", nama);
      if (imageFile) form.append("image", imageFile);

      await dialog.toastPromise(
        api.post("/products", form),
        {
          loading: "Menyimpan produk…",
          success: () => `Produk ditambahkan: ${kode}.`,
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menambah produk.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      setSuccess("Produk berhasil ditambahkan.");
      setKodeBarang("");
      setNamaBarang("");
      setImageFile(null);

      navigate("/products");
    } catch (e2) {
      const msg = e2?.response?.data?.message || e2.message || "Gagal menambah produk";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in pb-10 max-w-[1400px]">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-zinc-600 shadow-sm">
            <PackagePlus className="h-3 w-3 text-sky-600" />
            Master Data
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-zinc-900">
            Tambah Produk Baru
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Isi kode barang dan nama barang. Kategori, jenis, seri, dan size dihitung otomatis dari format kode.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/products" className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 text-[13px] font-medium text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors focus:outline-none focus:ring-1 focus:ring-zinc-900">
            <ArrowLeft className="h-4 w-4" />
            Kembali
          </Link>
        </div>
      </header>

      {error ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">{error}</div>
      ) : null}
      {success ? (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-medium text-emerald-800">{success}</div>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 border-b border-zinc-100 bg-zinc-50/50 p-5 shrink-0">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white">
                <PackagePlus className="h-5 w-5 text-zinc-700" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold text-zinc-900">Data Utama Produk</h2>
                <p className="text-[12px] font-medium text-zinc-500 mt-0.5">Input kode dan nama, atribut lain akan diekstrak otomatis.</p>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field
                  label="Kode Barang"
                  hint="Contoh: A-TS-B1-M, B-PK-PTB1, C-TB-B1"
                >
                  <input
                    value={kodeBarang}
                    onChange={(e) => setKodeBarang(e.target.value.toUpperCase())}
                    placeholder="Masukkan kode barang"
                    className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-[13px] font-mono font-bold uppercase outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                  />
                  <div className="mt-2 min-h-[16px] text-[11px] font-bold">
                    {checkingKode ? (
                      <span className="text-zinc-500">Mengecek kode...</span>
                    ) : kodeWarning ? (
                      <span className="text-rose-600">{kodeWarning}</span>
                    ) : kodeBarang.trim() ? (
                      <span className="text-emerald-600">Kode unik & siap digunakan.</span>
                    ) : null}
                  </div>
                </Field>

                <Field label="Nama Barang">
                  <input
                    value={namaBarang}
                    onChange={(e) => setNamaBarang(e.target.value)}
                    placeholder="Masukkan nama barang"
                    className="w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-[13px] font-bold outline-none transition-all focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 shadow-sm"
                  />
                </Field>

                <div className="sm:col-span-2">
                  <Field
                    label="Foto Produk (opsional)"
                    hint="Pilih gambar dari PC. Format: PNG, JPG, WEBP. Akan memakai placeholder jika kosong."
                  >
                    <label className="flex min-h-[120px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 px-4 py-5 text-center transition-colors hover:border-zinc-400 hover:bg-white group">
                      <FileImage className="h-8 w-8 text-zinc-400 group-hover:text-zinc-500 transition-colors" />
                      <span className="mt-3 text-[13px] font-bold text-zinc-700">
                        {imageFile ? imageFile.name : "Klik untuk memilih foto produk"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                        className="sr-only"
                      />
                    </label>
                  </Field>
                </div>
              </div>

              <div className="mt-8 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
                <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest text-indigo-700 mb-4">
                  <Sparkles className="h-4 w-4" />
                  Preview Ekstraksi Kode
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <DerivedTile label="Kategori" value={derived.kategori} />
                  <DerivedTile label="Jenis" value={derived.jenis} />
                  <DerivedTile label="Seri" value={derived.seri} />
                  <DerivedTile label="Size" value={derived.size} />
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-100 bg-zinc-50/50 px-6 py-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
              <Link
                to="/products"
                className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-zinc-200 bg-white px-4 py-2 text-[13px] font-bold text-zinc-700 shadow-sm hover:bg-zinc-50 transition-colors"
              >
                Batal
              </Link>
              <button
                disabled={loading || !canSubmit}
                className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-zinc-900 px-6 py-2 text-[13px] font-bold text-white shadow-sm hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:scale-100 active:scale-95"
              >
                {loading ? (
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {loading ? "Menyimpan..." : "Simpan Produk"}
              </button>
            </div>
          </div>
        </form>

        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-zinc-200 bg-white shadow-sm overflow-hidden">
            <div className="flex items-center gap-3 border-b border-zinc-100 bg-zinc-50/50 p-5">
              <div className="flex h-10 w-10 items-center justify-center rounded-md border border-zinc-200 bg-white">
                <CheckCircle2 className="h-5 w-5 text-zinc-700" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold text-zinc-900">Status Validasi</h2>
                <p className="text-[12px] font-medium text-zinc-500 mt-0.5">Kesiapan data sebelum disimpan.</p>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3.5 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Kode</div>
                <div className="mt-1 break-all font-mono text-[13px] font-bold text-zinc-900">{kodeBarang.trim().toUpperCase() || "-"}</div>
              </div>
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3.5 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Nama</div>
                <div className="mt-1 text-[13px] font-bold text-zinc-900">{namaBarang.trim() || "-"}</div>
              </div>
              <div className={`rounded-md border p-3.5 text-[12px] font-bold shadow-sm ${canSubmit ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                {canSubmit ? "✓ Form valid dan siap disimpan." : "⚠ Lengkapi kode dan nama produk."}
              </div>
            </div>
          </div>

          <details className="rounded-lg border border-zinc-200 bg-white shadow-sm group">
            <summary className="cursor-pointer px-5 py-4 text-[14px] font-bold text-zinc-900 hover:bg-zinc-50 transition-colors select-none">
              Panduan Format Kode Brads
            </summary>

            <div className="border-t border-zinc-100 p-5 space-y-5 text-[13px] text-zinc-700 bg-zinc-50/30">
              <div className="overflow-hidden rounded-md border border-zinc-200 bg-white shadow-sm">
                <table className="min-w-full text-left text-[12px]">
                  <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
                    <tr>
                      <th className="px-4 py-2.5 font-bold">Jenis Produk</th>
                      <th className="px-4 py-2.5 font-bold">Kode Awalan</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 font-medium">
                    <tr><td className="px-4 py-2.5">Produk Utama</td><td className="px-4 py-2.5 font-mono font-bold text-zinc-900">A</td></tr>
                    <tr><td className="px-4 py-2.5">Packaging & Souvenir</td><td className="px-4 py-2.5 font-mono font-bold text-zinc-900">B</td></tr>
                    <tr><td className="px-4 py-2.5">Additional Item</td><td className="px-4 py-2.5 font-mono font-bold text-zinc-900">C</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="space-y-4">
                <div className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm">
                  <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-900 text-center mb-1">
                    Struktur: Produk Utama
                  </div>
                  <div className="mb-4 text-center text-[11px] font-medium text-zinc-500 font-mono">
                    A - [Kategori] - [Seri] - [Ukuran]
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="overflow-hidden rounded-md border border-zinc-200">
                      <table className="min-w-full text-[11px] text-left">
                        <thead className="bg-zinc-50 border-b border-zinc-200 text-zinc-600">
                          <tr><th className="px-3 py-2 font-bold">Kategori</th><th className="px-3 py-2 font-bold">Singkatan</th></tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100 font-medium">
                          <tr><td className="px-3 py-2">T-Shirt</td><td className="px-3 py-2 font-mono font-bold">TS</td></tr>
                          <tr><td className="px-3 py-2">Kemeja</td><td className="px-3 py-2 font-mono font-bold">SS</td></tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-[11px] font-medium text-zinc-500">
                Gunakan panduan referensi lengkap pada dokumen operasional untuk jenis dan kategori lainnya.
              </div>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}
